import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { format, isAfter, isValid, parseISO, startOfDay } from "date-fns";
import { Formik, FormikErrors, FormikProps } from "formik";
import { EMPTY_CONTEXT, ENGLISH_ABBREV } from "@bto/shared";
import { Button } from "../shared/Button/Button";
import {
  Spinner,
  CreateAccountPasswordInfoContainer,
  CreateAccountPrivacyPolicy,
  CreateAccountCenterFormPreTestTitle,
} from "./CreateAccountForm.styles";

import { CreateAccountPreTestRequest } from "./Services/CreateAccountService";
import { CreateAccountValuesType } from "./types";
import {
  LANDING_PAGE_URL,
  LOGIN_PAGE_URL,
  VERIFY_EMAIL_URL,
} from "../../utils/urlConstants";
import { createAccountInitialValues } from "./Services/CreateAccountServiceHelpers";
import { CreateAccountPasswordInfo } from "./CreateAccountPasswordInfo";
import { getPasswordRequirements } from "./passwordRequirements";
import loading from "../../assets/icons/loading.svg";
import { patientState } from "../../recoil/atoms";
import { UserAuthClientResponse } from "../../recoil/types";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { validateEmail } from "../../utils/hooks/helpers";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import { getUserAuth } from "../../recoil/selectors";

import { StyledInput } from "../shared/UI-Library/StyledInput";
import {
  CreateAccountCenterPreTestContainer,
  CreateAccountCenterFormPreTestContainer,
  CreateAccountFormPreTestStyledContainer,
  CreateAccountFormPreTestStyled,
  CreateAccountCenterFormPreTestRowWrapper,
  CreateAccountPretestButtonContainer,
  CreateAccountPretestLogin,
  CreateAccountPretestLoginContent,
  CreateAccountPretestLoginButton,
  CreateAccountFormPreTestStyledWrapper,
  CreateAccountFormPreTestStyledFormWrapper,
  CreateAccountCenterFormPreTestError,
} from "./CreateAccountPreTestForm.styles";
import { StyledUISelect } from "../shared/UI-Library/StyledUISelect";
import { WarningHollowRound } from "../../assets/iconComponents/WarningHollowRound";
import { setCookieAccessToken } from "../../utils/requestUtils";
import { normalizeLanguage } from "../../i18n/languagePreference";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

export const CreateAccountFormContentPreTest: React.FC<{
  setPathname: () => void;
}> = ({ setPathname }) => {
  const { t, i18n } = useTranslation();
  // Pre-test signup is also pre-auth: evaluate the flag against the LDProvider's
  // anonymous context. This form has no language dropdown, but the flag still
  // gates the submitted language so a stale ?lang=es / i18nextLng=es never
  // persists a Spanish preference while the feature is off.
  const { enabled: isLanguageToggleEnabled } = useBooleanFeatureFlag(
    FeatureFlags.PatientPortalSpanishLanguageToggle,
    EMPTY_CONTEXT,
    "anonymous",
  );
  const history = useHistory();
  const auth = useRecoilValue(getUserAuth);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setPathname();
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(() => {
    if (auth) {
      history.push(LANDING_PAGE_URL);
    }
  }, [auth]);

  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const [, setPatientInfo] = useRecoilState(patientState);

  const { deviceType } = useDeviceHook();
  const { portalAccountPublicId } = useParams<{
    portalAccountPublicId: string | undefined;
  }>();

  const onSubmit = async ({
    first_name,
    last_name,
    middle_name,
    gender,
    referral,
    zip,
    dob,
    email,
    password,
  }: CreateAccountValuesType): Promise<void> => {
    if (submissionInFlight.current) return;
    submissionInFlight.current = true;
    setIsSubmitting(true);
    try {
      const response = await CreateAccountPreTestRequest({
        first_name,
        last_name,
        dob,
        email,
        password,
        middle_name,
        gender,
        referral,
        zip,
        portal_account_public_id: portalAccountPublicId,
        // Gated on the flag so the feature stays fully off when disabled — this
        // form has no dropdown, so otherwise a session ?lang=es would persist.
        language: isLanguageToggleEnabled
          ? normalizeLanguage(i18n.language)
          : ENGLISH_ABBREV,
      });
      const statusCode: number = response[0];
      const newUserInfo: UserAuthClientResponse | undefined = response[1];
      if (newUserInfo?.auth0.access_token) {
        setCookieAccessToken(newUserInfo.auth0.access_token);
        setPatientInfo(newUserInfo);
        history.push(VERIFY_EMAIL_URL);
      } else {
        let msg = "";
        switch (statusCode) {
          case 400:
            msg = t("createAccount.errors.detailsMismatch");
            break;
          case 404:
            msg = t("createAccount.errors.unknownSignupLink");
            break;
          case 406:
            msg = t("createAccount.errors.expiredSignupLink");
            break;
          case 409:
            msg = t("createAccount.errors.accountCreation");
            break;
          case 412:
            msg = t("createAccount.errors.verifyPersonalDetails");
            break;
          case 500:
            msg = t("createAccount.errors.unknownRegistration");
            break;
          case 501:
            msg = t("createAccount.errors.network");
            break;
          default:
            break;
        }
        setErrorMessage(msg);
      }
    } catch (e) {
      setErrorMessage(t("createAccount.errors.network"));
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <CreateAccountCenterPreTestContainer
      data-testid="CreateAccountForm"
      deviceType={deviceType}
    >
      <ErrorPopup message={errorMessage} hideMessage={setErrorMessage} onTop />
      <CreateAccountCenterFormPreTestContainer>
        <CreateAccountCenterFormPreTestTitle id="title">
          {t("createAccount.preTest.title")}
        </CreateAccountCenterFormPreTestTitle>
        <Formik
          initialValues={createAccountInitialValues}
          onSubmit={onSubmit}
          validateOnChange={false}
          validate={(values) => {
            const errors: FormikErrors<CreateAccountValuesType> = {};
            if (!values.first_name) {
              errors.first_name = t("createAccount.errors.required");
            }
            if (!values.last_name) {
              errors.last_name = t("createAccount.errors.required");
            }
            if (!values.dob) {
              errors.dob = t("createAccount.errors.required");
            } else if (
              !isValid(parseISO(values.dob)) ||
              isAfter(parseISO(values.dob), startOfDay(new Date()))
            ) {
              errors.dob = t("createAccount.errors.invalidDate");
            }

            if (!values.email) {
              errors.email = t("createAccount.errors.required");
            } else if (!validateEmail(values.email)) {
              errors.email = t("createAccount.errors.invalidEmail");
            }

            if (!values.password) {
              errors.password = t("createAccount.errors.required");
            } else if (
              !Object.values(getPasswordRequirements(values.password)).every(
                Boolean,
              )
            ) {
              errors.password = t("createAccount.errors.passwordNotValid");
            }

            if (!values.confirm_password) {
              errors.confirm_password = t("createAccount.errors.required");
            } else if (values.confirm_password !== values.password) {
              errors.confirm_password = t(
                "createAccount.errors.passwordsDoNotMatch",
              );
            }

            if (Object.keys(errors).length) {
              document.getElementById("title")?.scrollIntoView();
            }

            return errors;
          }}
        >
          {({
            values,
            errors,
            handleSubmit,
            setFieldValue,
            setFieldError,
          }: FormikProps<CreateAccountValuesType>) => (
            <CreateAccountFormPreTestStyledWrapper deviceType={deviceType}>
              <CreateAccountFormPreTestStyledFormWrapper
                deviceType={deviceType}
              >
                {Boolean(
                  Object.keys(errors).filter((error) =>
                    Boolean(errors[error as keyof CreateAccountValuesType]),
                  ).length,
                ) && (
                  <CreateAccountCenterFormPreTestError>
                    <WarningHollowRound />
                    {t("createAccount.preTest.requiredFieldsPrompt")}
                  </CreateAccountCenterFormPreTestError>
                )}
                <CreateAccountFormPreTestStyledContainer
                  deviceType={deviceType}
                >
                  <CreateAccountFormPreTestStyled
                    deviceType={deviceType}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        !isSubmitting &&
                        e.target instanceof HTMLInputElement
                      ) {
                        handleSubmit();
                      }
                    }}
                  >
                    <CreateAccountCenterFormPreTestRowWrapper>
                      <StyledInput
                        fieldTitle={t("createAccount.fields.firstName")}
                        type="text"
                        name="first_name"
                        value={values.first_name}
                        onChange={(event) => {
                          setFieldError("first_name", "");
                          setFieldValue("first_name", event.target.value);
                        }}
                        error={errors.first_name}
                      />
                      <StyledInput
                        fieldTitle={t("createAccount.fields.middleName")}
                        type="text"
                        name="middle_name"
                        value={values.middle_name}
                        onChange={(event) => {
                          setFieldError("middle_name", "");
                          setFieldValue("middle_name", event.target.value);
                        }}
                        optional
                      />
                    </CreateAccountCenterFormPreTestRowWrapper>
                    <CreateAccountCenterFormPreTestRowWrapper
                      style={{ width: "100%" }}
                    >
                      <StyledInput
                        fieldTitle={t("createAccount.fields.lastName")}
                        type="text"
                        name="last_name"
                        value={values.last_name}
                        onChange={(event) => {
                          setFieldError("last_name", "");
                          setFieldValue("last_name", event.target.value);
                        }}
                        error={errors.last_name}
                      />
                      <StyledUISelect
                        fieldTitle={t("createAccount.fields.gender")}
                        optional
                        minWidth="200px"
                        name="gender"
                        value={values.gender}
                        onChange={(event) => {
                          setFieldError("gender", "");
                          setFieldValue("gender", event.target.value);
                        }}
                      >
                        <option value="Male">
                          {t("createAccount.gender.male")}
                        </option>
                        <option value="Female">
                          {t("createAccount.gender.female")}
                        </option>
                      </StyledUISelect>
                    </CreateAccountCenterFormPreTestRowWrapper>
                    <CreateAccountCenterFormPreTestRowWrapper>
                      <StyledInput
                        fieldTitle={t("createAccount.fields.email")}
                        type="text"
                        name="email"
                        value={values.email}
                        onChange={(event) => {
                          setFieldError("email", "");
                          setFieldValue("email", event.target.value);
                        }}
                        error={errors.email}
                      />
                      <StyledInput
                        fieldTitle={t("createAccount.fields.dateOfBirth")}
                        type="date"
                        max={format(new Date(), "yyyy-MM-dd")}
                        name="dob"
                        value={values.dob}
                        onChange={(event) => {
                          setFieldError("dob", "");
                          setFieldValue("dob", event.target.value);
                        }}
                        error={errors.dob}
                      />
                    </CreateAccountCenterFormPreTestRowWrapper>
                    <CreateAccountCenterFormPreTestRowWrapper>
                      <StyledInput
                        fieldTitle={t("createAccount.fields.zipCode")}
                        optional
                        type="text"
                        name="zip"
                        value={values.zip}
                        onChange={(event) => {
                          setFieldError("zip", "");
                          setFieldValue("zip", event.target.value);
                        }}
                      />
                      <StyledInput
                        fieldTitle={t("createAccount.fields.referralCode")}
                        optional
                        type="text"
                        name="referral"
                        value={values.referral}
                        onChange={(event) => {
                          setFieldError("referral", "");
                          setFieldValue("referral", event.target.value);
                        }}
                        error={errors.referral}
                      />
                    </CreateAccountCenterFormPreTestRowWrapper>
                    <CreateAccountCenterFormPreTestRowWrapper>
                      <StyledInput
                        fieldTitle={t("createAccount.fields.password")}
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={(event) => {
                          setFieldError("password", "");

                          setFieldValue("password", event.target.value);
                        }}
                        error={errors.password}
                      />
                      <StyledInput
                        fieldTitle={t("createAccount.fields.confirmPassword")}
                        type="password"
                        name="confirm_password"
                        value={values.confirm_password}
                        onChange={(event) => {
                          setFieldError("confirm_password", "");
                          setFieldValue("confirm_password", event.target.value);
                        }}
                        error={errors.confirm_password}
                      />
                    </CreateAccountCenterFormPreTestRowWrapper>

                    <CreateAccountPasswordInfoContainer>
                      <CreateAccountPasswordInfo
                        {...getPasswordRequirements(values.password)}
                        isFlatLayout={deviceType === "desktop"}
                      />
                    </CreateAccountPasswordInfoContainer>

                    <CreateAccountPrivacyPolicy>
                      <p>
                        {t("createAccount.legal.prefix")}
                        <a
                          style={{ textDecoration: "underline" }}
                          href="https://www.billiontoone.com/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("createAccount.legal.privacyPolicy")}
                        </a>
                        {t("createAccount.legal.preTestFinalSeparator")}
                        <a
                          style={{ textDecoration: "underline" }}
                          href="../../client/terms_of_use"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("createAccount.legal.termsOfUse")}
                        </a>
                      </p>
                    </CreateAccountPrivacyPolicy>
                  </CreateAccountFormPreTestStyled>
                </CreateAccountFormPreTestStyledContainer>
              </CreateAccountFormPreTestStyledFormWrapper>

              <CreateAccountPretestButtonContainer deviceType={deviceType}>
                {isSubmitting ? (
                  <Spinner>
                    <img src={loading} alt="Loading" />
                  </Spinner>
                ) : (
                  <Button
                    type="submit"
                    label={t("createAccount.buttons.createAccount")}
                    size="medium"
                    onClick={() => handleSubmit()}
                    backgroundColor="greenDark"
                  />
                )}
                <CreateAccountPretestLogin deviceType={deviceType}>
                  <CreateAccountPretestLoginContent>
                    {t("createAccount.alreadyHaveAccount")}
                  </CreateAccountPretestLoginContent>
                  <CreateAccountPretestLoginButton
                    onClick={() => history.push(LOGIN_PAGE_URL)}
                  >
                    {t("createAccount.login")}
                  </CreateAccountPretestLoginButton>
                </CreateAccountPretestLogin>
              </CreateAccountPretestButtonContainer>
            </CreateAccountFormPreTestStyledWrapper>
          )}
        </Formik>
      </CreateAccountCenterFormPreTestContainer>
    </CreateAccountCenterPreTestContainer>
  );
};
